import { useQuery } from '@tanstack/react-query';
import React, { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { CheckPill, FilterDescription, FilterGroup, FilterLabel, FilterSeparator, SelectPill } from '../../../../../../../components';
import { useDebounce } from '../../../../../../../hooks';
import { mapDispatchToProps, mapStateToProps, StoreTypeWithoutAllStores } from '../../../../../../../reducers/ipa/limits/filters/store';
import { checkIfIsLoading } from '../../../../../../../utils';
import { StoreType } from '../../../RegrasDePreco/lib/data';
import { listClusterDataQuery, listStoreDataQuery, listStoreTypeDataQuery } from '../../pages/RegisterLimitPage/service';

type StoreFilterProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const StoreFilter = memo(
  ({
    selectType,
    data: { query, data, cache, value },
    setSelectType,
    setStoreQueryByKey,
    setStoreValueByKey,
    addStoreDataByKey,
    setStoreCacheByKey,
    resetStore,
    resetStoreQueryByKey,
    resetStoreValueByKey,
    resetStoreCacheByKey,
  }: StoreFilterProps) => {
    const debounce = useDebounce();

    const handleSuccess = (key: StoreTypeWithoutAllStores) => {
      return (data: DataItem[]) => {
        addStoreDataByKey(key, [...(cache[key] ?? []), ...data]);
      };
    };

    const handleSelectStoreType = useCallback(
      (value: StoreType) => {
        resetStore();
        setSelectType(value);
      },
      [setSelectType, resetStore],
    );

    const handleSearch = useCallback(
      (key: StoreTypeWithoutAllStores) => {
        return (query: string) => {
          debounce(() => setStoreQueryByKey(key, query));
        };
      },
      [debounce, setStoreQueryByKey],
    );

    const handleClose = useCallback(
      (key: StoreTypeWithoutAllStores) => {
        return () => {
          setStoreQueryByKey(key, '');
        };
      },
      [setStoreQueryByKey],
    );

    const handleChangeStore = useCallback(
      (key: StoreTypeWithoutAllStores) => {
        return (value: string[]) => {
          setStoreValueByKey(key, value);

          const dataItemList = data[key].filter(dataItem => value.includes(dataItem.value));

          if (dataItemList.length) setStoreCacheByKey(key, dataItemList);
        };
      },
      [data, setStoreValueByKey, setStoreCacheByKey],
    );

    const handleCleanStore = useCallback(
      (key: StoreTypeWithoutAllStores) => {
        return () => {
          resetStoreQueryByKey(key);
          resetStoreValueByKey(key);
          resetStoreCacheByKey(key);
        };
      },
      [resetStoreQueryByKey, resetStoreValueByKey, resetStoreCacheByKey],
    );

    const { data: storeTypeData = [] } = useQuery(listStoreTypeDataQuery());

    const { fetchStatus: storeDataFetchStatus } = useQuery({
      ...listStoreDataQuery(query['individual-store']),
      onSuccess: handleSuccess('individual-store'),
      enabled: selectType === 'individual-store',
    });

    const { fetchStatus: clusterDataFetchStatus } = useQuery({
      ...listClusterDataQuery(query['clusters']),
      onSuccess: handleSuccess('clusters'),
      enabled: selectType === 'clusters',
    });

    return (
      <FilterGroup asChild>
        <li>
          <FilterLabel>Lojas</FilterLabel>

          <SelectPill data={storeTypeData} value={selectType} onSelect={handleSelectStoreType} searchable={false} cleanable={false} />

          {selectType === 'all-stores' ? <FilterDescription>Você também pode escolher lojas específicas</FilterDescription> : <FilterSeparator />}

          {selectType === 'individual-store' && (
            <>
              <CheckPill
                data={data['individual-store']}
                value={value['individual-store']}
                onSearch={handleSearch('individual-store')}
                onClose={handleClose('individual-store')}
                onChange={handleChangeStore('individual-store')}
                isLoading={checkIfIsLoading(storeDataFetchStatus)}
                onClean={handleCleanStore('individual-store')}
              />
              {!value['individual-store']?.length && <FilterDescription>Selecione uma loja</FilterDescription>}
            </>
          )}

          {selectType === 'clusters' && (
            <>
              <CheckPill
                data={data['clusters']}
                value={value['clusters']}
                onSearch={handleSearch('clusters')}
                onClose={handleClose('clusters')}
                onChange={handleChangeStore('clusters')}
                isLoading={checkIfIsLoading(clusterDataFetchStatus)}
                onClean={handleCleanStore('clusters')}
              />
              {!value['clusters'].length && <FilterDescription>Selecione um cluster</FilterDescription>}
            </>
          )}
        </li>
      </FilterGroup>
    );
  },
);

StoreFilter.displayName = 'StoreFilter';

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StoreFilter));
